/**
 * Mobile Optimization Service
 * 
 * Handles mobile-specific optimizations for:
 * - Battery efficiency
 * - Network data reduction
 * - Performance improvements
 * - Offline capabilities
 */

// ==================== Types ====================

export interface MobileOptimizationConfig {
    enableDataSaver: boolean;
    enableBatterySaver: boolean;
    cacheResponses: boolean;
    compressionLevel: 'none' | 'low' | 'medium' | 'high';
}

export interface NetworkStats {
    totalDataUsed: number; // in bytes
    requestCount: number;
    cacheHitRate: number; // 0-1
    averageRequestSize: number;
}

// ==================== Detection ====================

export const isMobileDevice = (): boolean => {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

export const isCapacitorApp = (): boolean => {
    return !!(window as any).Capacitor;
};

export const isMobileEnvironment = (): boolean => {
    return isMobileDevice() || isCapacitorApp();
};

// Check if user has data saver enabled (browser level)
export const hasDataSaverEnabled = (): boolean => {
    const connection = (navigator as any).connection;
    return connection?.saveData === true;
};

// Get battery status if available
export const getBatteryLevel = async (): Promise<number | null> => {
    try {
        if ('getBattery' in navigator) {
            const battery: any = await (navigator as any).getBattery();
            return battery.level; // 0-1
        }
    } catch (e) {
        // Battery API not available
    }
    return null;
};

export const isBatteryLow = async (): Promise<boolean> => {
    const level = await getBatteryLevel();
    return level !== null && level < 0.2; // Below 20%
};

// ==================== Response Caching ====================

class ResponseCache {
    private cache: Map<string, { response: string; timestamp: number; size: number }>;
    private readonly MAX_CACHE_SIZE = 50; // Max cached responses
    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    private totalDataSaved: number = 0;

    constructor() {
        this.cache = new Map();
        this.loadCacheStats();
    }

    // Generate cache key from user message
    generateKey(message: string): string {
        // Normalize message for cache key
        const normalized = message.toLowerCase().trim().replace(/\s+/g, ' ');
        // Simple hash for cache key
        let hash = 0;
        for (let i = 0; i < normalized.length; i++) {
            hash = ((hash << 5) - hash) + normalized.charCodeAt(i);
            hash = hash & hash; // Convert to 32bit integer
        }
        return hash.toString(36);
    }

    // Check if response is cached
    get(key: string): string | null {
        const cached = this.cache.get(key);
        if (!cached) return null;

        // Check if expired
        if (Date.now() - cached.timestamp > this.CACHE_DURATION) {
            this.cache.delete(key);
            return null;
        }

        console.log(`[MobileOptimization] Cache HIT - saved ${cached.size} bytes`);
        this.totalDataSaved += cached.size;
        this.saveCacheStats();
        return cached.response;
    }

    // Store response in cache
    set(key: string, response: string): void {
        // Evict oldest if cache full
        if (this.cache.size >= this.MAX_CACHE_SIZE) {
            const oldestKey = this.cache.keys().next().value;
            this.cache.delete(oldestKey);
        }

        this.cache.set(key, {
            response,
            timestamp: Date.now(),
            size: response.length
        });

        console.log(`[MobileOptimization] Cached response (${response.length} bytes)`);
    }

    // Clear expired entries
    cleanup(): void {
        const now = Date.now();
        for (const [key, value] of this.cache.entries()) {
            if (now - value.timestamp > this.CACHE_DURATION) {
                this.cache.delete(key);
            }
        }
    }

    // Get cache statistics
    getStats() {
        return {
            cacheSize: this.cache.size,
            totalDataSaved: this.totalDataSaved,
            maxCacheSize: this.MAX_CACHE_SIZE
        };
    }

    // Persist cache stats
    private saveCacheStats(): void {
        try {
            localStorage.setItem('luca_cache_stats', JSON.stringify({
                totalDataSaved: this.totalDataSaved,
                lastUpdate: Date.now()
            }));
        } catch (e) {
            // Ignore storage errors
        }
    }

    // Load cache stats
    private loadCacheStats(): void {
        try {
            const stats = localStorage.getItem('luca_cache_stats');
            if (stats) {
                const parsed = JSON.parse(stats);
                this.totalDataSaved = parsed.totalDataSaved || 0;
            }
        } catch (e) {
            // Ignore storage errors
        }
    }

    // Clear all cache
    clear(): void {
        this.cache.clear();
        console.log('[MobileOptimization] Cache cleared');
    }
}

export const responseCache = new ResponseCache();

// ==================== Network Data Tracking ====================

class NetworkDataTracker {
    private stats: NetworkStats;
    private readonly STATS_KEY = 'luca_network_stats';

    constructor() {
        this.stats = this.loadStats();
        this.setupPeriodicSave();
    }

    // Track outgoing request
    trackRequest(payloadSize: number): void {
        this.stats.totalDataUsed += payloadSize;
        this.stats.requestCount += 1;
        this.stats.averageRequestSize = this.stats.totalDataUsed / this.stats.requestCount;
    }

    // Track response received
    trackResponse(responseSize: number): void {
        this.stats.totalDataUsed += responseSize;
    }

    // Track cache hit
    trackCacheHit(): void {
        const totalRequests = this.stats.requestCount;
        if (totalRequests > 0) {
            // Increase cache hit rate
            this.stats.cacheHitRate = (this.stats.cacheHitRate * (totalRequests - 1) + 1) / totalRequests;
        }
    }

    // Get current stats
    getStats(): NetworkStats {
        return { ...this.stats };
    }

    // Get formatted stats for display
    getFormattedStats(): string {
        const mb = (this.stats.totalDataUsed / (1024 * 1024)).toFixed(2);
        const avgKb = (this.stats.averageRequestSize / 1024).toFixed(2);
        const hitRate = (this.stats.cacheHitRate * 100).toFixed(1);
        
        return `Total: ${mb}MB | Avg: ${avgKb}KB | Cache: ${hitRate}%`;
    }

    // Reset stats
    reset(): void {
        this.stats = {
            totalDataUsed: 0,
            requestCount: 0,
            cacheHitRate: 0,
            averageRequestSize: 0
        };
        this.saveStats();
    }

    private loadStats(): NetworkStats {
        try {
            const saved = localStorage.getItem(this.STATS_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
            console.warn('[NetworkTracker] Failed to load stats:', e);
        }

        return {
            totalDataUsed: 0,
            requestCount: 0,
            cacheHitRate: 0,
            averageRequestSize: 0
        };
    }

    private saveStats(): void {
        try {
            localStorage.setItem(this.STATS_KEY, JSON.stringify(this.stats));
        } catch (e) {
            console.warn('[NetworkTracker] Failed to save stats:', e);
        }
    }

    private setupPeriodicSave(): void {
        // Save stats every 30 seconds
        setInterval(() => this.saveStats(), 30000);
    }
}

export const networkTracker = new NetworkDataTracker();

// ==================== Battery Optimization ====================

// Reduce animation frame rate when battery is low
export const optimizeAnimations = (enabled: boolean): void => {
    const root = document.documentElement;
    if (enabled) {
        root.style.setProperty('--animation-duration', '0s');
        root.style.setProperty('--transition-duration', '0s');
    } else {
        root.style.removeProperty('--animation-duration');
        root.style.removeProperty('--transition-duration');
    }
};

// Reduce polling frequency when battery is low
export const getOptimalPollingInterval = async (): Promise<number> => {
    const isLowBattery = await isBatteryLow();
    if (isLowBattery) {
        return 60000; // 1 minute when battery low
    }
    return 30000; // 30 seconds normally
};

// ==================== Data Saver Mode ====================

export const getCompressionConfig = (level: 'none' | 'low' | 'medium' | 'high') => {
    switch (level) {
        case 'none':
            return {
                maxHistoryMessages: 20,
                maxMessageLength: null,
                compressOldMessages: false,
                enableCache: false
            };
        case 'low':
            return {
                maxHistoryMessages: 10,
                maxMessageLength: 2000,
                compressOldMessages: true,
                enableCache: true
            };
        case 'medium':
            return {
                maxHistoryMessages: 5,
                maxMessageLength: 1000,
                compressOldMessages: true,
                enableCache: true
            };
        case 'high':
            return {
                maxHistoryMessages: 3,
                maxMessageLength: 500,
                compressOldMessages: true,
                enableCache: true
            };
    }
};

// ==================== Auto-optimization based on conditions ====================

export const getRecommendedOptimizationLevel = async (): Promise<'none' | 'low' | 'medium' | 'high'> => {
    const isMobile = isMobileEnvironment();
    const hasDataSaver = hasDataSaverEnabled();
    const isLowBattery = await isBatteryLow();

    // Check network quality
    const connection = (navigator as any).connection;
    const effectiveType = connection?.effectiveType;
    const isSlowNetwork = effectiveType === 'slow-2g' || effectiveType === '2g';

    if (!isMobile) {
        return 'none'; // Desktop doesn't need optimization
    }

    if (hasDataSaver || isSlowNetwork) {
        return 'high'; // Aggressive optimization
    }

    if (isLowBattery) {
        return 'medium'; // Moderate optimization for battery
    }

    if (effectiveType === '3g') {
        return 'low'; // Light optimization for 3G
    }

    return 'none'; // 4G/5G with good battery
};

// ==================== Exports ====================

export default {
    isMobileDevice,
    isCapacitorApp,
    isMobileEnvironment,
    hasDataSaverEnabled,
    getBatteryLevel,
    isBatteryLow,
    responseCache,
    networkTracker,
    optimizeAnimations,
    getOptimalPollingInterval,
    getCompressionConfig,
    getRecommendedOptimizationLevel
};
